import { useState } from 'react';

import { SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import usePlannerStore from '../store';
import { AssignmentStatus } from '../types/assignment-status';
import { UtilizationBasedOn } from '../types/utilization-based-on';

export const PlannerPreferencesDropdown = () => {
  const { t } = useTranslation(['authenticated.planner']);
  const plannerPreferences = usePlannerStore(store => store.plannerPreferences);
  const setPlannerPreferences = usePlannerStore(store => store.setPlannerPreferences);

  const [localBasedOn, setLocalBasedOn] = useState<UtilizationBasedOn>(
    plannerPreferences.utilizationBasedOn
  );
  const [localStatuses, setLocalStatuses] = useState<AssignmentStatus[]>(
    plannerPreferences.assignmentStatuses || []
  );
  const [localShowFilteredOut, setLocalShowFilteredOut] = useState<boolean>(
    plannerPreferences.showFilteredOutAssignments ?? true
  );
  const [localIgnorePlaceholders, setLocalIgnorePlaceholders] = useState<boolean>(
    plannerPreferences.ignorePlaceholdersForGroup ?? false
  );
  const [isOpen, setIsOpen] = useState(false);

  const handleBasedOnChange = (value: string) => {
    setLocalBasedOn(value as UtilizationBasedOn);
  };

  const handleStatusToggle = (status: AssignmentStatus) => {
    const isCurrentlySelected = localStatuses.includes(status);

    const newStatuses = isCurrentlySelected
      ? localStatuses.filter(s => s !== status)
      : [...localStatuses, status];

    setLocalStatuses(newStatuses);
  };

  const isStatusChecked = (status: AssignmentStatus) => {
    return localStatuses.includes(status);
  };

  const handleApply = () => {
    setPlannerPreferences({
      utilizationBasedOn: localBasedOn,
      assignmentStatuses: localStatuses,
      showFilteredOutAssignments: localShowFilteredOut,
      ignorePlaceholdersForGroup: localIgnorePlaceholders,
    });
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Reset local state to store values when opening
      setLocalBasedOn(plannerPreferences.utilizationBasedOn);
      setLocalStatuses(plannerPreferences.assignmentStatuses || []);
      setLocalShowFilteredOut(plannerPreferences.showFilteredOutAssignments ?? true);
      setLocalIgnorePlaceholders(plannerPreferences.ignorePlaceholdersForGroup ?? false);
    }
    setIsOpen(open);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant={'outline'}>
          <SettingsIcon />
          {t('preferences')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t('plannerPreferences.basedOn.label')}</DropdownMenuLabel>
        <div className="px-2 py-2">
          <RadioGroup value={localBasedOn} onValueChange={handleBasedOnChange}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value={UtilizationBasedOn.TotalCapacity} id="totalCapacity" />
              <label
                htmlFor="totalCapacity"
                className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('plannerPreferences.basedOn.totalCapacity')}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value={UtilizationBasedOn.BillableCapacity} id="billableCapacity" />
              <label
                htmlFor="billableCapacity"
                className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('plannerPreferences.basedOn.billableCapacity')}
              </label>
            </div>
          </RadioGroup>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('plannerPreferences.filters.label')}</DropdownMenuLabel>
        <div className="px-2 py-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="confirmed"
                checked={isStatusChecked(AssignmentStatus.Confirmed)}
                onCheckedChange={() => handleStatusToggle(AssignmentStatus.Confirmed)}
              />
              <label
                htmlFor="confirmed"
                className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('plannerPreferences.filters.confirmed')}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="unconfirmed"
                checked={isStatusChecked(AssignmentStatus.Unconfirmed)}
                onCheckedChange={() => handleStatusToggle(AssignmentStatus.Unconfirmed)}
              />
              <label
                htmlFor="unconfirmed"
                className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('plannerPreferences.filters.unconfirmed')}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="draft"
                checked={isStatusChecked(AssignmentStatus.Draft)}
                onCheckedChange={() => handleStatusToggle(AssignmentStatus.Draft)}
              />
              <label
                htmlFor="draft"
                className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('plannerPreferences.filters.draft')}
              </label>
            </div>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('plannerPreferences.filtersAndGrouping.label')}</DropdownMenuLabel>
        <div className="px-2 py-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="showFilteredOutAssignments"
              checked={localShowFilteredOut}
              onCheckedChange={checked => setLocalShowFilteredOut(!!checked)}
            />
            <label
              htmlFor="showFilteredOutAssignments"
              className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t('plannerPreferences.showFilteredOutAssignments')}
            </label>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('plannerPreferences.placeholders.label')}</DropdownMenuLabel>
        <div className="px-2 py-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="ignorePlaceholdersForGroup"
              checked={localIgnorePlaceholders}
              onCheckedChange={checked => setLocalIgnorePlaceholders(!!checked)}
            />
            <label
              htmlFor="ignorePlaceholdersForGroup"
              className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t('plannerPreferences.placeholders.ignoreInGroup')}
            </label>
          </div>
        </div>
        <DropdownMenuSeparator />
        <div className="px-2 py-2">
          <div className="flex justify-end">
            <Button size="sm" onClick={handleApply}>
              {t('plannerPreferences.apply')}
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
